import React, { useEffect } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArrowLeft, ArrowRight, Clock, MapPin, ExternalLink } from 'lucide-react';
import Meta from '../../components/Meta';
import { renderInlineMarkdown } from '../../components/blog/MarkdownInline';
import {
  getPlanningArticlePhotoItems,
  PlanningArticleInlinePhoto,
  type PlanningArticlePhotoItem,
} from '../../components/blog/PlanningArticlePhotos';
import { posts, getPostBySlug, type BlogPost, type BlogSection } from '../../lib/blog/posts';
import { SITE_URL } from '../../lib/siteUrl';
import { fetchApplicationById } from '../../lib/services/applicationsApiService';
import {
  buildPlanningArticleRequestHeaders,
  fetchGeneratedPlanningArticleBySlug,
  fetchGeneratedPlanningBlogPosts,
  generatedArticleToBlogPost,
  type GeneratedPlanningArticle,
} from '../../lib/planningArticles';
import { formatBlogDate } from '../../lib/blog/date';

const PlanningArticleMap = dynamic(() => import('../../components/blog/PlanningArticleMap'), {
  ssr: false,
});

function RenderSection({ section }: { section: BlogSection; index: number }) {
  switch (section.type) {
    case 'paragraph':
      return (
        <p className="text-[17px] leading-[1.78] text-[var(--c-text-secondary)]">
          {renderInlineMarkdown(section.text)}
        </p>
      );

    case 'heading':
      return (
        <h2 className="mt-2 font-['Newsreader'] text-[26px] font-medium leading-tight tracking-[-0.01em] text-[var(--c-text-primary)]">
          {renderInlineMarkdown(section.text)}
        </h2>
      );

    case 'list':
      return (
        <ul className="space-y-2.5">
          {section.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[17px] leading-relaxed text-[var(--c-text-secondary)]">
              <span className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-[var(--c-accent)]" />
              <span>{renderInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );

    case 'bullets':
      return (
        <ul className="space-y-2">
          {section.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-[var(--c-text-secondary)]">
              <span className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-[var(--c-accent)]" />
              <span>{renderInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );

    case 'stats':
      return (
        <dl className="grid grid-cols-1 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] sm:grid-cols-3">
          {section.items.map((stat, i) => (
            <div
              key={i}
              className={`p-5 ${i > 0 ? 'border-t border-[var(--c-border)] sm:border-l sm:border-t-0' : ''}`}
            >
              <dd className="pr-numeral text-3xl leading-none">{stat.value}</dd>
              <dt className="pr-data mt-2 text-[10px] uppercase tracking-[0.14em]">{stat.label}</dt>
            </div>
          ))}
        </dl>
      );

    case 'callout':
      return (
        <div className="border border-[var(--c-border)] border-l-2 border-l-[var(--c-accent)] bg-[var(--c-surface)] px-6 py-5">
          <p className="text-base leading-relaxed text-[var(--c-text-secondary)]">{renderInlineMarkdown(section.text)}</p>
          {section.href && section.linkLabel && (
            <Link href={section.href} className="pr-button mt-4">
              {section.linkLabel}
              {section.href.startsWith('http') && <ExternalLink className="h-3.5 w-3.5" />}
            </Link>
          )}
        </div>
      );

    case 'divider':
      return <hr className="border-[var(--c-border)]" />;

    default:
      return null;
  }
}

function getPhotoInsertTargets(sectionCount: number, photoCount: number) {
  if (!sectionCount || !photoCount) return [];
  if (photoCount === 1) return [Math.min(1, sectionCount - 1)];

  return [
    Math.min(1, sectionCount - 1),
    Math.min(Math.max(3, Math.floor(sectionCount * 0.45)), sectionCount - 1),
    Math.min(Math.max(5, Math.floor(sectionCount * 0.72)), sectionCount - 1),
  ].slice(0, photoCount);
}

function renderArticleSectionsWithPhotos(sections: BlogSection[], photos: PlanningArticlePhotoItem[]) {
  const nodes: React.ReactNode[] = [];
  const targets = getPhotoInsertTargets(sections.length, photos.length);
  let photoIndex = 0;

  sections.forEach((section, index) => {
    nodes.push(<RenderSection key={`section-${index}`} section={section} index={index} />);

    if (
      photoIndex < photos.length &&
      index >= targets[photoIndex] &&
      section.type !== 'heading' &&
      section.type !== 'divider'
    ) {
      nodes.push(
        <PlanningArticleInlinePhoto
          key={`photo-${photoIndex}`}
          item={photos[photoIndex]}
          priority={photoIndex === 0}
          className="my-8"
        />
      );
      photoIndex += 1;
    }
  });

  while (photoIndex < photos.length) {
    nodes.push(
      <PlanningArticleInlinePhoto
        key={`photo-${photoIndex}`}
        item={photos[photoIndex]}
        priority={photoIndex === 0}
        className="my-8"
      />
    );
    photoIndex += 1;
  }

  return nodes;
}

interface Props {
  post: BlogPost;
  prevPost: BlogPost | null;
  nextPost: BlogPost | null;
  generatedArticle: GeneratedPlanningArticle | null;
  planningApplication: Record<string, unknown> | null;
}

function stripMarkdown(value: string) {
  return String(value || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getPlanningArticleImages(planningApplication: Record<string, unknown> | null) {
  return getPlanningArticlePhotoItems(planningApplication, 4).map((item) => item.thumbnailUrl);
}

function buildArticleStructuredData(post: BlogPost, generatedArticle: GeneratedPlanningArticle | null, planningApplication: Record<string, unknown> | null) {
  const canonicalPath = `/blog/${post.slug}`;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const articleImages = getPlanningArticleImages(planningApplication);
  const imageUrls = articleImages.length ? articleImages : [`${SITE_URL}/city-scope-social-v2.jpg`];
  const articleBody = stripMarkdown(
    post.sections
      .map((section) => {
        if ('text' in section && typeof section.text === 'string') return section.text;
        if ('items' in section && Array.isArray(section.items)) {
          return section.items
            .map((item: unknown) => {
              if (typeof item === 'string') return item;
              if (item && typeof item === 'object') {
                const record = item as { value?: unknown; label?: unknown };
                return `${record.value || ''} ${record.label || ''}`;
              }
              return '';
            })
            .join(' ');
        }
        return '';
      })
      .join(' ')
  );
  const keywords = generatedArticle?.seo?.keywordTargets?.length
    ? generatedArticle.seo.keywordTargets
    : post.tags;
  const applicationUrl = generatedArticle?.applicationPath
    ? `${SITE_URL}${generatedArticle.applicationPath}`
    : null;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${canonicalUrl}#article`,
        headline: post.title,
        description: post.excerpt,
        image: imageUrls,
        datePublished: post.publishedAt,
        dateModified: generatedArticle?.updatedAt || post.publishedAt,
        author: {
          '@type': 'Person',
          name: post.author,
        },
        publisher: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'City Scope',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/MainLogo.svg`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
        },
        isPartOf: {
          '@type': 'Blog',
          '@id': `${SITE_URL}/blog#journal`,
          name: 'City Scope Journal',
        },
        articleSection: generatedArticle ? 'Worth-watching planning applications' : 'Manual Journal posts',
        keywords,
        inLanguage: 'en-GB',
        wordCount: articleBody ? articleBody.split(/\s+/).length : undefined,
        articleBody: articleBody || undefined,
        ...(applicationUrl
          ? {
              about: {
                '@type': 'Thing',
                '@id': `${applicationUrl}#application`,
                name: `Planning application ${generatedArticle?.reference_no}`,
                identifier: generatedArticle?.reference_no,
                url: applicationUrl,
              },
              mentions: [
                {
                  '@type': 'Thing',
                  '@id': `${applicationUrl}#application`,
                  name: `Planning application ${generatedArticle?.reference_no}`,
                  identifier: generatedArticle?.reference_no,
                  url: applicationUrl,
                },
                ...(generatedArticle?.councilLink
                  ? [
                      {
                        '@type': 'WebPage',
                        name: 'Official council planning record',
                        url: generatedArticle.councilLink,
                      },
                    ]
                  : []),
              ],
            }
          : {}),
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: post.title,
        description: post.excerpt,
        inLanguage: 'en-GB',
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: imageUrls[0],
        },
        breadcrumb: {
          '@id': `${canonicalUrl}#breadcrumb`,
        },
        mainEntity: {
          '@id': `${canonicalUrl}#article`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Journal',
            item: `${SITE_URL}/blog`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: post.title,
            item: canonicalUrl,
          },
        ],
      },
    ],
  };
}

export default function BlogPostPage({ post, prevPost, nextPost, generatedArticle, planningApplication }: Props) {
  const articleStructuredData = buildArticleStructuredData(post, generatedArticle, planningApplication);
  const keywords = generatedArticle?.seo?.keywordTargets?.length
    ? generatedArticle.seo.keywordTargets.join(', ')
    : post.tags.join(', ');
  const articlePhotos = generatedArticle ? getPlanningArticlePhotoItems(planningApplication, 3) : [];
  const socialCardImage = `${SITE_URL}/og/blog/${encodeURIComponent(post.slug)}.png`;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.location.hash) return undefined;

    let frameId = 0;
    const timeoutIds: number[] = [];

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.getElementById('app-scroll-container')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    frameId = window.requestAnimationFrame(scrollToTop);
    timeoutIds.push(window.setTimeout(scrollToTop, 250));
    timeoutIds.push(window.setTimeout(scrollToTop, 900));

    return () => {
      window.cancelAnimationFrame(frameId);
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [post.slug]);

  return (
    <>
      <Meta
        title={generatedArticle?.seo?.seoTitle || `${post.title} | City Scope Journal`}
        description={generatedArticle?.seo?.metaDescription || post.excerpt}
        keywords={keywords}
        canonical={`/blog/${post.slug}`}
        ogImage={socialCardImage}
        ogImageAlt={`${post.title} - City Scope Journal social card`}
        ogImageType="image/png"
        ogType="article"
        author={post.author}
        publishedTime={post.publishedAt}
        modifiedTime={generatedArticle?.updatedAt}
        tags={post.tags}
        section="Planning Data"
        structuredData={articleStructuredData}
      />

      <div className="planning-light">
        <main className="article-page planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
          {/* ── Article header ─────────────────────────────────────────── */}
          <header className="border-b border-[var(--c-border)]">
            <div className="mx-auto max-w-[880px] px-6 pb-12 pt-[calc(var(--navbar-height,64px)+2.5rem)]">
              <Link
                href="/blog"
                className="inline-flex items-center gap-2 font-['DM_Mono'] text-[11px] uppercase tracking-[0.16em] text-[var(--c-text-tertiary)] transition-colors hover:text-[var(--c-accent)]"
              >
                <ArrowLeft className="h-3 w-3" /> The Journal
              </Link>

              <p className="pr-kicker pr-kicker--accent mt-8">
                CityScope &middot; Journal
              </p>

              <h1 className="mt-5 font-['Newsreader'] text-[clamp(2rem,5vw,3.25rem)] font-medium leading-[1.08] tracking-[-0.015em] text-[var(--c-text-primary)]">
                {post.title}
              </h1>

              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-[var(--c-text-secondary)]">
                {post.excerpt}
              </p>

              {post.tags.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span key={tag} className="pr-chip">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Byline */}
              <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--c-border)] pt-5">
                <span className="text-sm font-medium text-[var(--c-text-primary)]">{post.author}</span>
                <span className="flex flex-wrap items-center gap-3 font-['DM_Mono'] text-[11px] uppercase tracking-[0.1em] text-[var(--c-text-tertiary)]">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-2.5 w-2.5" /> {post.location}
                  </span>
                  <span aria-hidden>&middot;</span>
                  <span>{formatBlogDate(post.publishedAt)}</span>
                  <span aria-hidden>&middot;</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" /> {post.readingTime} min read
                  </span>
                </span>
              </div>
            </div>
          </header>

          {/* ── Article body ───────────────────────────────────────────── */}
          <div className="mx-auto max-w-[780px] space-y-7 px-6 py-14">
            {generatedArticle?.applicationPath && (
              <section className="overflow-hidden border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)]">
                <div className="grid gap-0 sm:grid-cols-[1fr_auto]">
                  <div className="p-5 sm:p-6">
                    <div className="pr-kicker pr-kicker--accent">
                      Related planning application
                    </div>
                    <h2 className="mt-4 font-['Newsreader'] text-2xl font-medium leading-tight text-[var(--c-text-primary)]">
                      View the full application record
                    </h2>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
                      Open the live City Scope application page for documents, council links, tags, insights and status updates for reference {generatedArticle.reference_no}.
                    </p>
                  </div>
                  <div className="flex items-stretch border-t border-[var(--c-border)] sm:border-l sm:border-t-0">
                    <Link
                      href={generatedArticle.applicationPath}
                      className="group flex min-w-full items-center justify-between gap-5 bg-[var(--c-text-primary)] px-5 py-5 text-base font-medium text-[var(--c-bg)] transition-opacity hover:opacity-90 sm:min-w-[230px] sm:flex-col sm:items-start sm:justify-center sm:px-6"
                    >
                      <span>
                        Open application
                        <span className="block font-['DM_Mono'] text-[11px] font-normal uppercase tracking-[0.14em] opacity-70">
                          {generatedArticle.reference_no}
                        </span>
                      </span>
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                    </Link>
                  </div>
                </div>
              </section>
            )}

            {renderArticleSectionsWithPhotos(post.sections, articlePhotos)}

            {generatedArticle ? <PlanningArticleMap application={planningApplication} /> : null}
          </div>

          {/* ── Author card ────────────────────────────────────────────── */}
          <div className="mx-auto max-w-[780px] px-6 pb-8">
            <div className="pr-panel p-6">
              <p className="pr-section-label">About the author</p>
              <div className="mt-4">
                <div className="font-['Newsreader'] text-lg font-medium text-[var(--c-text-primary)]">
                  {post.author}
                </div>
                <div className="pr-data mt-1 text-[11px]">
                  {post.location}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                  Building City Scope — tools to make Edinburgh&apos;s planning data easier to search, understand and use.
                </p>
              </div>
            </div>
          </div>

          {/* ── Prev / Next ────────────────────────────────────────────── */}
          {(prevPost || nextPost) && (
            <div className="mx-auto grid max-w-[780px] grid-cols-1 gap-4 px-6 pb-16 sm:grid-cols-2">
              {prevPost ? (
                <Link
                  href={`/blog/${prevPost.slug}`}
                  className="group pr-panel p-5 transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)]"
                >
                  <div className="mb-2 flex items-center gap-1.5 font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
                    <ArrowLeft className="h-3 w-3" /> Previous
                  </div>
                  <div className="font-['Newsreader'] text-lg font-medium leading-snug text-[var(--c-text-primary)] transition-colors group-hover:text-[var(--c-accent)]">
                    {prevPost.title}
                  </div>
                </Link>
              ) : <div className="hidden sm:block" />}

              {nextPost ? (
                <Link
                  href={`/blog/${nextPost.slug}`}
                  className="group pr-panel p-5 transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)] sm:text-right"
                >
                  <div className="mb-2 flex items-center gap-1.5 font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)] sm:justify-end">
                    Next <ArrowRight className="h-3 w-3" />
                  </div>
                  <div className="font-['Newsreader'] text-lg font-medium leading-snug text-[var(--c-text-primary)] transition-colors group-hover:text-[var(--c-accent)]">
                    {nextPost.title}
                  </div>
                </Link>
              ) : <div className="hidden sm:block" />}
            </div>
          )}

          {/* ── Back to journal ────────────────────────────────────────── */}
          <div className="border-t border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="mx-auto flex max-w-[780px] items-center justify-between px-6 py-6">
              <Link href="/blog" className="pr-textlink">
                <ArrowLeft className="h-4 w-4" /> Back to The Journal
              </Link>
              <span className="pr-data text-[11px]">
                City Scope
              </span>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: posts.map((p) => ({ params: { slug: p.slug } })),
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = params?.slug as string;
  const manualPost = getPostBySlug(slug);
  // Manual posts are build inputs and must not depend on API availability.
  const generatedArticle = manualPost ? null : await fetchGeneratedPlanningArticleBySlug(slug);
  const generatedPost = generatedArticle ? generatedArticleToBlogPost(generatedArticle) : undefined;
  const post = generatedPost || manualPost;
  if (!post) {
    // A reference embedded in a slug does not identify its council. Do not
    // persist an Edinburgh redirect when a generated-article lookup has a
    // transient failure: non-Edinburgh references can share the same format.
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  const planningApplication = generatedArticle
    ? await fetchApplicationById(generatedArticle.reference_no, {
        retries: 1,
        timeoutMs: 15000,
        headers: buildPlanningArticleRequestHeaders(true),
        ...(generatedArticle.sourceCouncil ? { council: generatedArticle.sourceCouncil } : {}),
      }).catch(() => null)
    : null;
  const generatedPosts = await fetchGeneratedPlanningBlogPosts(100);
  const allPosts = [...generatedPosts, ...posts].sort(
    (left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime()
  );
  const idx = allPosts.findIndex((item) => item.slug === post.slug);
  return {
    props: {
      post,
      prevPost: idx > 0 ? allPosts[idx - 1] : null,
      nextPost: idx >= 0 && idx < allPosts.length - 1 ? allPosts[idx + 1] : null,
      generatedArticle,
      planningApplication,
    },
    revalidate: 300,
  };
};
